import { Box, Divider, Switch, Text } from "@mantine/core";
import React, { useEffect } from "react";
import useStyles from "./style";
import ActionButtons from "./ActionButtons";
import { getInitialAdminPermissions, initialStaffPermissions, initialContractorPermissions, PERMISSION_LABELS } from "../../utils";
import { useSelector } from "react-redux";

/**
 * Component to manage staff roles and display permissions.
 * @param {Object} props
 * @param {string} props.activeTab - Contains the selected staff's data info.
 * @param {Object} props.data - Contains the selected staff's data info.
 * @param {Object} props.mode - Contains the mode of the component like view or edit.
 * @param {Object} props.role - Contains the current role of the staff.
 * @param {Function} props.setRole - Function to set the role of the staff.
 * @param {Function} props.handleSave - Function to handle save action.
 * @param {Function} props.handleCancel - Function to handle cancel action.
 * @param {String} props.activeTab - Contains the selected tab.
 * @param {Boolean} props.roleError - Function to handle cancel action.
 */

export const StaffRolePermissionTab = ({
  data,
  mode,
  role,
  form,
  setRole,
  handleSave,
  handleCancel,
  activeTab,
  roleError,
  userpermissions,
  setUserPermissions,
}) => {

  const { classes, cx } = useStyles();
  const billing = useSelector((state) => {
    return state.featureFlags.billing;
  });

  // Initialize role based on valid values
  useEffect(() => {
    const validRoles = ["admin", /* "manager", */ "staff", "contractor"];
    if (validRoles.includes(data?.role)) {
      setRole(data.role);
    }

    if (data?.permissions && typeof data.permissions === "object") {
      // Merge defaults with backend data (in case some keys are missing)
      const mergedPermissions = {
        ...userpermissions,
        ...data.permissions,
      };
      setUserPermissions(mergedPermissions);
    }
  }, [data?.role]);

  // Handle role toggle
  const handleRoleChange = (newRole) => {
    if (role !== newRole) {
      setRole(newRole);
      if (newRole === "admin") {
        setUserPermissions(getInitialAdminPermissions(data));
      // } else if (newRole === "manager") {
      //   setUserPermissions(initialManagerPermissions);
      } else if (newRole === "contractor") {
        setUserPermissions(initialContractorPermissions);
      } else {
        setUserPermissions(initialStaffPermissions);
      }
    }
  };

  const handlePermissionToggle = (key) => {
    setUserPermissions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const staffHiddenKeys = ["invoice", "billing", "staff", "facility"];
  const filteredPermissions = Object.entries(userpermissions).filter(
    ([key]) => {
      if (key === "billing" && !billing) return false;
      if (role === "staff" && staffHiddenKeys.includes(key)) return false;
      return true;
    }
  );

  return (
    <Box>
      <Box className={classes.staffInfoContainer}>
        <Box px={20} pb={20}>
          <Box className={classes.rolePermissionLayout}>
            {/* Roles Column */}
            <Box className={classes.rolesColumn}>
              <Text className={classes.columnHeader}>Roles</Text>
              {["admin", /* "manager", */ "staff", /*"contractor" */ ].map((roleType) => (
                <Box
                  key={roleType}
                  className={cx(classes.roleCard, {
                    [classes.roleCardActive]: role === roleType,
                  })}
                  onClick={() => mode !== "view" && handleRoleChange(roleType)}
                >
                  <Switch
                    size="md"
                    color="yellow"
                    disabled={mode === "view"}
                    label={roleType.charAt(0).toUpperCase() + roleType.slice(1)}
                    labelPosition="left"
                    checked={role === roleType}
                    onChange={() => handleRoleChange(roleType)}
                    className={classes.swites}
                  />
                </Box>
              ))}
            </Box>

            {/* Permissions Column */}
            <Box className={classes.permissionsColumn}>
              <Text className={classes.columnHeader}>Permissions</Text>
              {filteredPermissions?.map(([key, value]) => (
                <Switch
                  key={key}
                  size="md"
                  color="yellow"
                  disabled={mode === "view"}
                  label={PERMISSION_LABELS[key] || key}
                  labelPosition="right"
                  checked={value}
                  onChange={() => handlePermissionToggle(key)}
                  className={classes.Permissionswites}
                />
              ))}
              <Switch
                key="Enable 2FA"
                size="md"
                color="yellow"
                disabled={mode === "view"}
                label="Enable 2FA"
                labelPosition="right"
                checked={form.values.is2FaAccess}
                onChange={() => form.setFieldValue("is2FaAccess", !form.values.is2FaAccess)}
                className={classes.Permissionswites}
              />
            </Box>
          </Box>
        </Box>

        {mode === "view" ? null : (
          <Box>
            <Divider my={10} />
            <ActionButtons
              onCancel={handleCancel}
              onSave={handleSave}
              type="submit"
              activeTab={activeTab}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
